import { useCallback } from "react";
import type { FileUploadOptions, FileUploadResult } from "../types";
import { useFileStorageValidator } from "./useFileStorageValidator";
import { useAsyncOperationProgress } from "@/components/progress/useAsyncOperationProgress";

export const useFileUpload = ({
  client,
  fieldName,
  onUploadSuccess,
  onUploadError,
}: FileUploadOptions): FileUploadResult => {
  const operationProgress = useAsyncOperationProgress();
  const { validateFiles } = useFileStorageValidator();

  const uploadFiles = useCallback(
    async (
      files: File[],
    ): Promise<{
      failedUploads: { file: File; error: unknown }[];
      validationError?: string;
    }> => {
      if (files.length === 0) {
        return { failedUploads: [] };
      }

      const validationError = await validateFiles(files);
      if (validationError) {
        return {
          failedUploads: [],
          validationError,
        };
      }

      const items = files.map((file, index) => ({
        id: `${file.name}-${index}`,
        label: file.name,
      }));

      operationProgress.initialize(items);
      operationProgress.startProcessing();

      const failedUploads: { file: File; error: unknown }[] = [];

      try {
        for (let i = 0; i < files.length; i++) {
          // isCancelling is not memoized, but safe to access directly
          // eslint-disable-next-line react-hooks/exhaustive-deps
          if (operationProgress.isCancelling()) {
            break;
          }

          const file = files[i];
          const uniqueId = `${file.name}-${i}`;
          operationProgress.setCurrentItem(file.name);

          try {
            const uploaded = await client.upload(fieldName, file, {
              skipValidation: true,
            });
            operationProgress.markItemProcessed(uniqueId, true);
            onUploadSuccess?.(file, uploaded);
          } catch (error) {
            operationProgress.markItemProcessed(uniqueId, false);
            failedUploads.push({ file, error });
            onUploadError?.(file, error);
          }
        }
      } finally {
        operationProgress.complete();
      }

      return { failedUploads };
    },
    // operationProgress is intentionally omitted - isCancelling is not memoized but safe to access
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, fieldName, onUploadSuccess, onUploadError, validateFiles],
  );

  return { uploadFiles, operationProgress };
};
